import { useRevalidator } from '@remix-run/react';
import { Building2 } from 'lucide-react';
import { useEffect, useState } from 'react';

interface OfficeOption {
  id: string;
  name: string;
}

interface OfficeSwitcherProps {
  offices: OfficeOption[];
  currentOfficeId: string;
  currentOfficeName: string;
  /** Increment to open the menu from outside, for example global search. */
  openSignal?: number;
}

export function OfficeSwitcher({
  offices,
  currentOfficeId,
  currentOfficeName,
  openSignal = 0,
}: OfficeSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { revalidate } = useRevalidator();

  useEffect(() => {
    if (openSignal > 0 && offices.length > 1) setIsOpen(true);
  }, [openSignal, offices.length]);

  if (!offices.length) return null;
  if (offices.length === 1) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <Building2 className="h-4 w-4" />
        <span className="font-medium">{currentOfficeName}</span>
      </div>
    );
  }

  const handleSelect = async (officeId: string) => {
    if (officeId === currentOfficeId) {
      setIsOpen(false);
      return;
    }
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('officeId', officeId);
      const res = await fetch('/api/switch-office', {
        method: 'POST',
        body: formData,
      });
      if (res.ok) {
        setIsOpen(false);
        revalidate();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(prev => !prev)}
        disabled={isSubmitting}
        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label="Switch office"
      >
        <Building2 className="h-4 w-4 text-gray-500" />
        <span className="max-w-[140px] truncate">{currentOfficeName}</span>
        <svg
          className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" aria-hidden onClick={() => setIsOpen(false)} />
          <ul
            className="absolute left-0 mt-1 py-1 w-56 max-h-60 overflow-auto bg-white rounded-lg shadow-lg border border-gray-200 z-20"
            role="listbox"
          >
            {offices.map(office => (
              <li key={office.id} role="option" aria-selected={office.id === currentOfficeId}>
                <button
                  type="button"
                  onClick={() => handleSelect(office.id)}
                  disabled={isSubmitting}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none ${
                    office.id === currentOfficeId
                      ? 'bg-purple-50 text-purple-800 font-medium'
                      : 'text-gray-700'
                  }`}
                >
                  {office.name}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
